import { FormikConfig } from 'formik';
import { memo, useCallback, useMemo, useState } from 'react';
import * as yup from 'yup';
import { FormikEmailInput } from '../../../components/formik/FormEmailInput';
import { FormikInput } from '../../../components/formik/FormInput';
import { CreateModal } from '../../../components/Modal/CreateModal';
import { ToastType, useToastNotifications } from '../../../hooks/useToastNotifications';
import { useValidationTranslations } from '../../../hooks/useValidationTranslations';
import { T, useT } from '../../../translation/src';
import { graphqlErrorCode } from '../../../utils/graphqlErrors';

export interface CustomerOrganisationValues {
  name: string;
  emails: string[];
}

export interface CreateCustomerOrganisationModalProps {
  onClose: () => void;
  open?: boolean;
  title: React.ReactNode;
  // Makes the organisation; throws to keep the dialog open (ALREADY_EXISTS lands on the name field).
  onCreate: (values: CustomerOrganisationValues) => Promise<void>;
}
// An organisation made for a customer by someone who will not be in it — a superadmin, a partner — with its
// name and the people who get it as admins. The same address field as the share dialog.
const CreateCustomerOrganisationModal_: React.FC2<CreateCustomerOrganisationModalProps> = ({ onClose, open, title, onCreate }) => {
  const { showToast } = useToastNotifications();
  const validation = useValidationTranslations();
  const nameLabel = useT('organisation name', { swc: true });
  const adminsLabel = useT('admins', { swc: true });
  const addPeople = useT('add people by email', { swc: true });
  const failed = useT('could not create the organisation', { swc: true });
  const taken = useT('an organisation with this name already exists', { swc: true });
  const [submitting, setSubmitting] = useState(false);

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        name: yup.string().trim().required(validation.isRequired),
        // Optional: an organisation can be set up first and people invited later.
        emails: yup.array().of(yup.string().email(validation.validEmail)),
      }),
    [validation.isRequired, validation.validEmail],
  );

  const onSubmit: FormikConfig<CustomerOrganisationValues>['onSubmit'] = useCallback(
    async (values, helpers) => {
      setSubmitting(true);
      try {
        await onCreate({ name: values.name.trim(), emails: values.emails });
        helpers.resetForm();
        onClose();
      } catch (e) {
        if (graphqlErrorCode(e) === 'ALREADY_EXISTS') helpers.setFieldError('name', taken);
        else showToast({ title: failed, text: e instanceof Error ? e.message : String(e), type: ToastType.ERROR });
      } finally {
        setSubmitting(false);
      }
    },
    [failed, onClose, onCreate, showToast, taken],
  );

  const formik: FormikConfig<CustomerOrganisationValues> = useMemo(() => ({ initialValues: { name: '', emails: [] }, onSubmit, validationSchema }), [onSubmit, validationSchema]);

  return (
    <CreateModal<CustomerOrganisationValues> open={open} onClose={onClose} title={title} createButtonTitle={<T _str="create" swc />} formik={formik} isSubmitting={submitting} noOverflow>
      <FormikInput name="name" label={nameLabel} placeholder={nameLabel} autoFocus />
      <div className="mt-4">
        <FormikEmailInput name="emails" label={adminsLabel} placeholder={addPeople} aria-label={addPeople} />
      </div>
      <div className="text-sm text-gray-400">
        <T _str="Optional. The people invited become its admins and get the organisation when they accept. You can also invite them later, once it is set up. You will not be in it." />
      </div>
    </CreateModal>
  );
};
export const CreateCustomerOrganisationModal = memo(CreateCustomerOrganisationModal_);
